import {
  Box,
  Text,
  Card,
  Column,
  Columns,
  TextLinkButton,
  Stack,
  Divider,
  Hidden,
} from 'braid-design-system';
import { Fragment } from 'react';

import type {
  AccountSelectionEdge,
  AdvertiserAccountOption,
} from 'src/graphql/generated';
import type { PendingAccountEmailStatuses } from 'src/views/pages/SelectAccountPage/SelectAccountPage';

import AccountActionButton from './components/AccountActionButton/AccountActionButton';
import AccountStatusIndicator from './components/AccountStatusIndicator/AccountStatusIndicator';

import { mobileColumn } from './AccountSegment.css';

interface AccountSegmentProps {
  heading: string;
  subheading: string;
  accounts: AccountSelectionEdge[];
  handleSelectAccount?: (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    node: AdvertiserAccountOption,
  ) => void;
  handleActivateAccount?: (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    node: AdvertiserAccountOption,
  ) => void;
  pendingAccountEmailStatuses?: PendingAccountEmailStatuses;
}

const AccountSegment = ({
  heading,
  subheading,
  accounts,
  handleSelectAccount,
  handleActivateAccount,
  pendingAccountEmailStatuses,
}: AccountSegmentProps) => {
  const displayAccountId = (
    seekId: string,
    billingId?: string | null,
  ): string => billingId ?? seekId;

  return (
    <Card>
      <Box padding="small">
        <Stack space="none">
          <Stack space="medium">
            {heading ? (
              <Text size="large" weight="medium">
                {heading}
              </Text>
            ) : null}
            {subheading ? <Text>{subheading}</Text> : null}
          </Stack>
          <Stack space="medium">
            <br />
            {accounts.map(({ node }) => (
              <Fragment key={node.seekId}>
                <Hidden below="tablet">
                  <Divider weight="regular" />
                </Hidden>
                <Columns space="xxsmall" alignY="top" collapseBelow="desktop">
                  <Column width="1/2">
                    <Box display="flex" alignItems="flexStart">
                      <Box marginRight="small">
                        <AccountStatusIndicator
                          isActive={Boolean(handleSelectAccount)}
                        />
                      </Box>

                      <Text size="small" weight="medium">
                        {handleSelectAccount ? (
                          <TextLinkButton
                            id={`${node.seekId}-selectable-link`}
                            onClick={(e) => handleSelectAccount(e, node)}
                          >
                            {node.name}
                          </TextLinkButton>
                        ) : (
                          node.name
                        )}
                      </Text>
                    </Box>
                  </Column>
                  <Column>
                    <Box
                      className={mobileColumn}
                      paddingTop={{ mobile: 'xxsmall', desktop: 'none' }}
                    >
                      <Text size="small">
                        {displayAccountId(node.seekId, node.billingId)}
                      </Text>
                    </Box>
                  </Column>
                  {handleActivateAccount ? (
                    <Column width="content">
                      <Box className={mobileColumn}>
                        <AccountActionButton
                          accountNode={node}
                          handleActivateAccount={handleActivateAccount}
                          pendingAccountEmailStatus={
                            pendingAccountEmailStatuses &&
                            pendingAccountEmailStatuses[node.seekId]
                          }
                        />
                      </Box>
                    </Column>
                  ) : null}
                </Columns>
                {node.childAccounts?.map((childAccountOption) => (
                  <Box marginLeft="medium" key={childAccountOption?.seekId}>
                    <Columns space="none">
                      <Box marginRight="small">
                        <AccountStatusIndicator
                          isActive={Boolean(handleSelectAccount)}
                        />
                      </Box>
                      <Column width="1/2">
                        {handleSelectAccount && (
                          <Text size="small" weight="medium">
                            <TextLinkButton
                              id={`${childAccountOption.seekId}-selectable-link`}
                              onClick={(e) =>
                                handleSelectAccount(e, childAccountOption)
                              }
                            >
                              {childAccountOption.name}
                            </TextLinkButton>
                          </Text>
                        )}
                      </Column>
                      <Column>
                        <Text size="small" weight="medium">
                          {displayAccountId(
                            childAccountOption.seekId,
                            childAccountOption.billingId,
                          )}
                        </Text>
                      </Column>
                    </Columns>
                  </Box>
                ))}
              </Fragment>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};

export default AccountSegment;
